// ============================================
// components/CoverUploader.tsx
// 作品封面上传:选图 → 前端压缩(canvas)→ base64 → 上传接口 → 预览.
// 存 fileID, 显示固定 URL, 换图传新文件名(不被缓存困扰).
// 仅作者本人可用(后端校验).
// ============================================

import { useRef, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { authedCallApi } from '../services/auth';

interface Props {
  /** 作品 short_id。已有作品（编辑页）传它，走 community-upload-cover；
   *  发布页作品还没创建，不传，走 community-upload-cover-temp（临时上传）。 */
  shortId?: string;
  /** 当前封面 URL(已有则预览) */
  value: string;
  /** 上传成功回调,传回新的固定 URL */
  onChange: (url: string) => void;
}

const MAX_DIM = 1200;        // 压缩后最长边
const TARGET_QUALITY = 0.82; // jpeg 质量

// 把图片文件压缩成 base64(去掉 data: 前缀)
function compressToBase64(file: File): Promise<{ base64: string; ext: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
          else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('canvas 不可用')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', TARGET_QUALITY);
        const base64 = dataUrl.split(',')[1] || '';
        resolve({ base64, ext: 'jpg' });
      };
      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}

export default function CoverUploader({ shortId, value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const pick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|png|webp)/.test(file.type)) {
      setError('仅支持 JPG / PNG / WebP');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const { base64, ext } = await compressToBase64(file);
      // 有 short_id（编辑已存在作品）→ 正式上传并入库；
      // 无 short_id（发布页，作品还没建）→ 临时上传，只返回 URL，发布时再随作品提交。
      const res = shortId
        ? await authedCallApi<{ cover_url: string }>(
            'community-upload-cover',
            { short_id: shortId, base64, ext },
          )
        : await authedCallApi<{ cover_url: string }>(
            'community-upload-cover-temp',
            { base64, ext },
          );
      if (!res.ok || !res.cover_url) {
        setError(res.message || res.error || '上传失败');
      } else {
        onChange(res.cover_url as string);
      }
    } catch (err) {
      setError((err as Error).message || '上传失败');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onFile}
      />
      <div
        onClick={pick}
        className="relative w-full aspect-video rounded-xl border-2 border-dashed border-ink-300/60 bg-paper overflow-hidden cursor-pointer hover:border-gold-400 transition-colors flex items-center justify-center"
      >
        {value ? (
          <img src={value} alt="封面" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-ink-400">
            <ImagePlus className="w-8 h-8" strokeWidth={1.5} />
            <span className="text-sm">点击上传封面</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-gold-600 animate-spin" />
          </div>
        )}
      </div>
      {value && (
        <button onClick={pick} disabled={uploading} className="text-sm text-gold-600 hover:underline mt-2">
          更换封面
        </button>
      )}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      <p className="text-xs text-ink-400 mt-1.5">JPG / PNG / WebP,自动压缩,建议横版图</p>
    </div>
  );
}
